import {
  JOBBOSS2_APP_ID,
  JOBBOSS2_PROVIDER_ID,
  JobBoss2ConnectionConfigSchema,
  type ConnectionProviderKey,
  type JobBoss2StoredCredentials,
} from "@forgepoint/shared";

/** Raw JobBOSS² connection dialog state before validation. */
export interface JobBoss2ConnectionFormValues {
  clientId: string;
  clientSecret: string;
  requiresEgressProxy: boolean;
}

/** The finalize request the JobBOSS² dialog submits once the form is valid. */
export interface JobBoss2FinalizeInput {
  connectionProviderId: ConnectionProviderKey;
  appId: string;
  credentials: JobBoss2StoredCredentials;
  requiresEgressProxy: boolean;
}

/** Either a user-facing validation message or the finalize request to send. */
export type JobBoss2ConnectionFormResult =
  | { error: string }
  | { input: JobBoss2FinalizeInput };

const MISSING_CREDENTIALS_ERROR =
  "Provide the JobBOSS² client ID and client secret.";

/**
 * Validates the JobBOSS² connection form and builds the finalize request.
 * @param values - Raw form values
 * @returns A validation error, or the finalize input
 */
export function buildJobBoss2FinalizeInput(
  values: JobBoss2ConnectionFormValues,
): JobBoss2ConnectionFormResult {
  if (!values.clientId.trim() || !values.clientSecret) {
    return { error: MISSING_CREDENTIALS_ERROR };
  }
  return {
    input: {
      connectionProviderId: JOBBOSS2_PROVIDER_ID,
      appId: JOBBOSS2_APP_ID,
      credentials: {
        type: "JobBoss2ServiceCredentials",
        clientId: values.clientId,
        clientSecret: values.clientSecret,
      },
      requiresEgressProxy: values.requiresEgressProxy,
    },
  };
}

/**
 * Reads the connected company description off a finalized JobBOSS² connection.
 * @param config - The connection's config column as returned by the API
 * @returns The company description, or null when the config is not a JobBOSS² config
 */
export function readJobBoss2CompanyDescription(config: unknown): string | null {
  const parsed = JobBoss2ConnectionConfigSchema.safeParse(config);
  if (!parsed.success) {
    return null;
  }
  return parsed.data.companyDescription ?? null;
}
